import { cloneElement, Fragment, isValidElement, useMemo } from "react";
import { useOptionalLocale } from "./LocaleProvider.jsx";

const TAG_PATTERN = /<(\w+)>(.*?)<\/\1>/g;

/**
 * Parse a translation template with safe component slots, e.g.
 * "By submitting, you agree to our <termsLink>Terms</termsLink>."
 */
export function parseTransTemplate(template, components = {}) {
  if (!template || typeof template !== "string") {
    return [template];
  }

  const parts = [];
  let lastIndex = 0;
  let match;

  TAG_PATTERN.lastIndex = 0;
  while ((match = TAG_PATTERN.exec(template)) !== null) {
    if (match.index > lastIndex) {
      parts.push(template.slice(lastIndex, match.index));
    }
    const tagName = match[1];
    const innerText = match[2];
    const component = components[tagName];
    if (component && isValidElement(component)) {
      parts.push(cloneElement(component, { key: `${tagName}-${match.index}` }, innerText));
    } else {
      parts.push(innerText);
    }
    lastIndex = TAG_PATTERN.lastIndex;
  }

  if (lastIndex < template.length) {
    parts.push(template.slice(lastIndex));
  }

  return parts.length ? parts : [template];
}

/**
 * Safe rich-text translation — never renders raw HTML from catalogs.
 *
 * @example
 * <Trans
 *   i18nKey="forms.consentWithLinks"
 *   components={{
 *     termsLink: <a href="/terms" />,
 *     privacyLink: <a href="/privacy" />,
 *   }}
 * />
 */
export function Trans({ i18nKey, components = {}, values = {}, fallback = null, t: tOverride }) {
  const localeContext = useOptionalLocale();
  const adapter = localeContext?.translation;

  const nodes = useMemo(() => {
    let template = null;
    if (adapter?.getRaw) {
      template = adapter.getRaw(i18nKey);
    } else if (localeContext?.t) {
      template = localeContext.t(i18nKey, values);
    }
    if (!template) {
      return fallback != null ? [fallback] : [i18nKey];
    }
    if (Object.keys(values).length) {
      template = localeContext?.t ? localeContext.t(i18nKey, values) : template;
    }
    return parseTransTemplate(template, components);
  }, [adapter, localeContext, i18nKey, components, values, fallback]);

  return (
    <>
      {nodes.map((node, index) =>
        typeof node === "string" ? <Fragment key={`text-${index}`}>{node}</Fragment> : node
      )}
    </>
  );
}

export default Trans;
